import React, {
  createContext,
  useRef,
  useContext,
  ReactNode,
} from "react";

interface ScrollContextType {
  benefitsRef: React.RefObject<HTMLDivElement | null>;
  blogRef: React.RefObject<HTMLDivElement | null>;
  videoSliderRef: React.RefObject<HTMLDivElement | null>;
  InfoCardRef: React.RefObject<HTMLDivElement | null>;
  planesRef: React.RefObject<HTMLDivElement | null>;
  scrollToRef: (ref: React.RefObject<HTMLDivElement | null>) => void;
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const benefitsRef = useRef<HTMLDivElement | null>(null);
  const blogRef = useRef<HTMLDivElement | null>(null);
  const videoSliderRef = useRef<HTMLDivElement | null>(null);
  const InfoCardRef = useRef<HTMLDivElement | null>(null);
  const planesRef = useRef<HTMLDivElement | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (!ref.current) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const HEADER_OFFSET = 80; // altura del header
    const targetPosition = ref.current.offsetTop - HEADER_OFFSET;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 1200;
    let start: number | null = null;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animation = (currentTime: number) => {
      if (start === null) start = currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = easeInOutCubic(progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        rafIdRef.current = requestAnimationFrame(animation);
      } else {
        rafIdRef.current = null;
      }
    };

    rafIdRef.current = requestAnimationFrame(animation);
  };

  return (
    <ScrollContext.Provider
      value={{
        benefitsRef,
        blogRef,
        videoSliderRef,
        InfoCardRef,
        planesRef,
        scrollToRef,
      }}
    >
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error("useScroll debe usarse dentro de un ScrollProvider");
  }
  return context;
};
